import type { ReactNode } from 'react'
import type { Ecriture, LigneBalance, Resultat } from '../../api/compta'
import type { DonneesOps } from '../../api/types'
import { Icone, type NomIcone } from '../Icone'

/** Ce que reçoit chaque sous-onglet de la comptabilité — calculé UNE fois par l'écran. */
export interface PropsCompta {
  donnees: DonneesOps
  orgId: string
  ecritures: Ecriture[]
  soldes: LigneBalance[]
  compte: Resultat
  /** Date jusqu'à laquelle les écritures sont verrouillées (clôture), ou null. */
  verrou: string | null
  surNotifier: (m: string) => void
  surRafraichir: () => Promise<void>
}

/** Export CSV — séparateur point-virgule et BOM, pour qu'Excel ouvre droit. */
export function exporterCSV(nom: string, lignes: (string | number)[][], surNotifier: (m: string) => void) {
  const csv =
    '﻿' +
    lignes
      .map((l) =>
        l
          .map((c) => {
            const s = c == null ? '' : String(c)
            return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
          })
          .join(';'),
      )
      .join('\n')
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  a.download = nom
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  surNotifier(`Export « ${nom} » téléchargé`)
}

export function Carte({
  titre,
  sous,
  icone,
  actions,
  surExport,
  flush = true,
  children,
}: {
  titre: string
  sous?: ReactNode
  icone?: NomIcone
  actions?: ReactNode
  surExport?: () => void
  flush?: boolean
  children: ReactNode
}) {
  return (
    <div className="card" data-anim>
      <div className="card-head">
        <div>
          <h3>
            {icone && (
              <span className="h-ic">
                <Icone nom={icone} />
              </span>
            )}{' '}
            {titre}
          </h3>
          {sous && <div className="ch-sub">{sous}</div>}
        </div>
        <span className="sp" />
        {actions}
        {surExport && (
          <button className="btn btn-outline btn-sm" onClick={surExport} style={{ marginLeft: 8 }}>
            <Icone nom="download" /> Exporter
          </button>
        )}
      </div>
      <div className={`card-body${flush ? ' flush' : ''}`}>{children}</div>
    </div>
  )
}

export function Table({
  entetes,
  lignes,
  vide,
  droite = [],
  pied,
}: {
  entetes: string[]
  lignes: ReactNode[][]
  vide: string
  /** Index des colonnes numériques, alignées à droite. */
  droite?: number[]
  pied?: ReactNode[]
}) {
  const st = (j: number) => (droite.includes(j) ? { textAlign: 'right' as const } : undefined)
  return (
    <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            {entetes.map((e, j) => (
              <th key={`${e}-${j}`} style={st(j)}>
                {e}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 ? (
            <tr>
              <td colSpan={entetes.length} className="t-muted">
                {vide}
              </td>
            </tr>
          ) : (
            lignes.map((l, i) => (
              <tr key={i}>
                {l.map((c, j) => (
                  <td key={j} style={st(j)}>
                    {c}
                  </td>
                ))}
              </tr>
            ))
          )}
          {pied && lignes.length > 0 && (
            <tr style={{ background: 'var(--card)' }}>
              {pied.map((c, j) => (
                <td key={j} style={{ ...st(j), fontWeight: 800 }}>
                  {c}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

/** Ligne libellé / valeur (`.rl` de l'original). */
export function Ligne({ libelle, valeur, fort = false, couleur }: { libelle: ReactNode; valeur: ReactNode; fort?: boolean; couleur?: string | undefined }) {
  return (
    <div className="rl" style={fort ? { borderTop: '2px solid var(--ink)', marginTop: 4, paddingTop: 9 } : undefined}>
      <span style={{ flex: 1, fontWeight: fort ? 800 : undefined }}>{libelle}</span>
      <b style={couleur ? { color: couleur } : undefined}>{valeur}</b>
    </div>
  )
}

export function CodeCompte({ code }: { code: string }) {
  return <span className="acc-code">{code}</span>
}
